import { useFormContext } from "./formElements";
import { useMemo, useState } from "react";
import { Button, Form, Row, Col, Table, Alert, InputGroup, OverlayTrigger, Tooltip } from "react-bootstrap";

/**
 * CustomTables
 * -------------------------------------------------------
 * Komponent do tworzenia i edycji wielu customowych tabel.
 * - Użytkownik podaje liczbę wierszy i kolumn -> tworzy nową tabelę
 * - Wszystkie komórki tabeli są edytowalne (Form.Control)
 * - Każda tabela ma dodatkowe pole opisu
 * - Nowa tabela jest dodawana do nadrzędnego elementu (props.value) w polu `value` jako kolejny element
 * - Można usunąć dowolnie dodaną tabelę
 * -------------------------------------------------------
 * Oczekiwany kształt `element` (props.value):
 * {
 *   value: Array<{
 *     id: string,
 *     description: string,
 *     rows: number,
 *     cols: number,
 *     headers: string[],
 *     hideInPdf?: boolean[], // długość = cols; true = kolumna ukryta w PDF
 *     data: string[][]
 *   }>
 * }
 */

export default function CustomTables({ value: element }) {
  const { onChange } = useFormContext();

  // lokalne stany formularza tworzenia nowej tabeli
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const [error, setError] = useState("");

  // bezpieczny odczyt listy tabel
  const tables = useMemo(() => Array.isArray(element?.value) ? element.value : [], [element.value]);

  const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

  const createEmptyMatrix = (r, c) => Array.from({ length: r }, () => Array.from({ length: c }, () => ""));

  const addTable = () => {
    const r = clamp(Number(rows) || 0, 1, 100);
    const c = clamp(Number(cols) || 0, 1, 100);

    if (!Number.isInteger(r) || !Number.isInteger(c) || r <= 0 || c <= 0) {
      setError("Podaj prawidłowe liczby dla wierszy i kolumn (min. 1).");
      return;
    }

    const newTable = {
      id: `tbl_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      description: "",
      rows: r,
      cols: c,
      headers: Array.from({ length: c }, (_, i) => `Kol ${i + 1}`),
      hideInPdf: Array.from({ length: c }, () => false),
      data: createEmptyMatrix(r, c),
    };

    element.value = [...tables, newTable];
    onChange(element);
    setError("");
  };

  const removeTable = (tableId) => {
    
    const next = tables.filter((t) => t.id !== tableId);
    element.value = next;
    onChange(element);
  };

  const updateCell = (tableIndex, rIdx, cIdx, value) => {
    const next = tables.map((t, idx) => {
      if (idx !== tableIndex) return t;
      const nextData = t.data.map((row, rr) => (rr === rIdx ? row.map((cell, cc) => (cc === cIdx ? value : cell)) : row));
      return { ...t, data: nextData };
    });
    element.value = next;
    onChange(element);
  };

  const updateDescription = (tableIndex, value) => {
    const next = tables.map((t, idx) => (idx === tableIndex ? { ...t, description: value } : t));
    element.value = next;
    onChange(element);
  };

  const updateHeader = (tableIndex, colIndex, value) => {
    const next = tables.map((t, idx) => {
      if (idx !== tableIndex) return t;
      const nextHeaders = (t.headers && t.headers.length === t.cols)
        ? [...t.headers]
        : Array.from({ length: t.cols }, (_, i) => t.headers?.[i] ?? `Kol ${i + 1}`);
      nextHeaders[colIndex] = value;
      return { ...t, headers: nextHeaders };
    });
    element.value = next;
    onChange(element);
  };

  const updateHideInPdf = (tableIndex, colIndex, hidden) => {
    const next = tables.map((t, idx) => {
      if (idx !== tableIndex) return t;
      const nextHideInPdf = Array.from({ length: t.cols }, (_, i) => !!t.hideInPdf?.[i]);
      nextHideInPdf[colIndex] = hidden;
      return { ...t, hideInPdf: nextHideInPdf };
    });
    element.value = next;
    onChange(element);
  };

  return (
    <div className="p-2">


      {tables.map((tbl, tIndex) => (
        <div key={tbl.id} className="mb-4 border rounded p-2">
          <Row className="g-2 align-items-center mb-2">
            <Col>
              <InputGroup>
                <InputGroup.Text>Opis</InputGroup.Text>
                <Form.Control
                  placeholder="Opis tabeli"
                  value={tbl.description}
                  onChange={(e) => updateDescription(tIndex, e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col xs="auto">
              <Button variant="outline-danger" onClick={() => removeTable(tbl.id)}>
                Usuń tabelę
              </Button>
            </Col>
          </Row>

          <div className="overflow-auto">
            <Table bordered hover size="sm" className="mb-0">
              <thead>
                <tr>
                  {Array.from({ length: tbl.cols }).map((_, c) => {
                    const headerInput = (
                      <Form.Control
                        className="border-0 text-center bg-light"
                        type="text"
                        value={tbl.headers?.[c] ?? `Kol ${c + 1}`}
                        onChange={(e) => updateHeader(tIndex, c, e.target.value)}
                      />
                    );
                    const hidden = !!tbl.hideInPdf?.[c];

                    return (
                      <th key={`h-${tbl.id}-${c}`} className="p-0">
                        {Array.isArray(tbl.hideInPdf) ? (
                          <InputGroup>
                            {headerInput}
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>{hidden ? "niewidoczne w PDF" : "widoczne w PDF"}</Tooltip>}
                            >
                              <InputGroup.Text className="border-0 bg-light">
                                <Form.Check
                                  type="checkbox"
                                  className="m-0"
                                  checked={!hidden}
                                  onChange={(e) => updateHideInPdf(tIndex, c, !e.target.checked)}
                                />
                              </InputGroup.Text>
                            </OverlayTrigger>
                          </InputGroup>
                        ) : (
                          headerInput
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: tbl.rows }).map((_, r) => (
                  <tr key={`r-${tbl.id}-${r}`}>
                    {Array.from({ length: tbl.cols }).map((_, c) => (
                      <td key={`c-${tbl.id}-${r}-${c}`} className="p-0">
                        <Form.Control
                          className="border-0 rounded-0"
                          type="text"
                          value={tbl.data?.[r]?.[c] ?? ""}
                          placeholder={`${tbl.headers?.[c]}`}
                          onChange={(e) => updateCell(tIndex, r, c, e.target.value)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      ))}

      
      <Row className="align-items-end g-2 mb-3">
        <Col xs="auto">
          <Form.Label className="mb-1">Liczba wierszy</Form.Label>
          <Form.Control
            type="number"
            min={1}
            max={100}
            value={rows}
            onChange={(e) => setRows(e.target.value)}
          />
        </Col>
        <Col xs="auto">
          <Form.Label className="mb-1">Liczba kolumn</Form.Label>
          <Form.Control
            type="number"
            min={1}
            max={100}
            value={cols}
            onChange={(e) => setCols(e.target.value)}
          />
        </Col>
        <Col xs="auto">
          <Button className="mt-4" onClick={addTable}>
            Utwórz tabelę
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="py-2">
          {error}
        </Alert>
      )}

      {tables.length === 0 && (
        <Alert variant="info" className="py-2">
          Brak tabel. Dodaj pierwszą tabelę powyżej.
        </Alert>
      )}
    </div>
  );
}
